import React, { useContext, useState, lazy, Suspense, useMemo, useCallback, useRef } from 'react';
import { Menu, MenuItem, Box, Typography, Collapse, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { ThemeContext } from 'styled-components';
import { useGroup } from '../../../providers/GroupProvider';

/** Synthetic id for the single "Default Bill" row in quick actions (replaces multiple shadow bills in default group). */
const DEFAULT_BILL_MENU_ID = '__default_bill__';
import { useQuery } from 'react-query';
import { listUsersTemplates } from '../../../services/template/Templates';
import { listTemplateStatements } from '../../../services/template/Statements';
import { listTemplateTransactions } from '../../../services/template/Transactions';
// Removed unused imports - balance is now returned directly from the API
import { CsrfContext } from '../../../providers/CsrfProvider';
import { AuthContext } from '../../auth/providers/AuthProvider';
import { CaretLeft, CaretRight, CaretDown } from '@phosphor-icons/react';
import TransactionModal from '../../template/modals/ModalTransactions';
import FormatNumber from '../../../utils/formatters/FormatNumber';

// Balance is now returned directly from the API in bill.participants[0].balance
// No need for BillBalanceCalculator component anymore

// Lazy load the modals to avoid import issues
const NewStatementModal = lazy(() => import('../../template/modals/NewStatementModal'));
const QuickSplitModal = lazy(() => import('../../template/modals/QuickSplitModal'));

const QuickActionsMenu = ({ anchorEl, open, onClose }) => {
    const theme = useContext(ThemeContext);
    const { groups } = useGroup();
    const { csrfToken } = useContext(CsrfContext);
    const { authClaims } = useContext(AuthContext);
    const [showNewChargeSubmenu, setShowNewChargeSubmenu] = useState(false);
    const [newChargeMenuAnchor, setNewChargeMenuAnchor] = useState(null);
    const [expandedGroupId, setExpandedGroupId] = useState(null);
    const [selectedBill, setSelectedBill] = useState(null);
    const [showStatementModal, setShowStatementModal] = useState(false);

    // Make Payment state
    const [showMakePaymentSubmenu, setShowMakePaymentSubmenu] = useState(false);
    const [makePaymentMenuAnchor, setMakePaymentMenuAnchor] = useState(null);
    const [expandedPaymentGroupId, setExpandedPaymentGroupId] = useState(null);
    const [selectedPaymentBill, setSelectedPaymentBill] = useState(null);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    // Take Payment state
    const [showTakePaymentSubmenu, setShowTakePaymentSubmenu] = useState(false);
    const [takePaymentMenuAnchor, setTakePaymentMenuAnchor] = useState(null);
    const [expandedTakePaymentGroupId, setExpandedTakePaymentGroupId] = useState(null);
    const [selectedTakePaymentBill, setSelectedTakePaymentBill] = useState(null);
    const [showTakePaymentUserMenu, setShowTakePaymentUserMenu] = useState(false);
    const [takePaymentUserMenuAnchor, setTakePaymentUserMenuAnchor] = useState(null);
    const [selectedTakePaymentUser, setSelectedTakePaymentUser] = useState(null);
    const [showTakePaymentModal, setShowTakePaymentModal] = useState(false);

    // Quick Split (default bill statement) state
    const [showQuickSplitModal, setShowQuickSplitModal] = useState(false);

    // Filter to only unarchived groups
    const unarchivedGroups = (groups || []).filter(group => !group.archived_at);

    // Use groups as-is, no renaming
    const allGroupsForMenu = unarchivedGroups;

    // Fetch bills for the expanded group (New Charge)
    // expandedGroupId can be 'default_group' (null) or a group UUID
    const { data: billsData } = useQuery(
        ['templates', expandedGroupId],
        () => listUsersTemplates(csrfToken, expandedGroupId === 'default_group' ? null : expandedGroupId),
        {
            enabled: !!expandedGroupId && !!csrfToken,
            staleTime: 5 * 60 * 1000,
        }
    );

    const bills = billsData || [];

    // Helper function to get favorite status
    const getFavoriteStatus = (bill) => {
        // If user is the owner, always use bill-level favorite
        if (bill.owner_sub && authClaims?.user_sub && 
            bill.owner_sub.toUpperCase() === authClaims.user_sub.toUpperCase()) {
            return bill.favorite || false;
        }
        
        // If user is a participant (but not owner), use their participant favorite status
        if (bill.participants && bill.participants.length > 0 && authClaims?.user_sub) {
            const userParticipant = bill.participants.find(
                p => p.user_sub && p.user_sub.toUpperCase() === authClaims.user_sub.toUpperCase()
            );
            if (userParticipant && userParticipant.favorite !== undefined) {
                return userParticipant.favorite;
            }
        }
        
        return false;
    };

    // Filter to unarchived bills and sort by favorite status (favorites first), then by created_at (newest first)
    const sortedBills = useMemo(() => {
        if (!bills || bills.length === 0) return [];

        // Filter to only unarchived bills
        const unarchivedBills = bills.filter(bill => !bill.archived_at || bill.archived_at === '');

        // Sort by favorite first, then by created_at date (newest first)
        return unarchivedBills.sort((a, b) => {
            const favoriteA = getFavoriteStatus(a);
            const favoriteB = getFavoriteStatus(b);
            if (favoriteA !== favoriteB) {
                return favoriteA ? -1 : 1;
            }
            const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
            const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
            return dateB - dateA; // Newest first
        });
    }, [bills]);

    // In default group: exclude shadow (is_default) bills so we show one "Default Bill" row instead
    const billsInDefaultGroup = useMemo(() => {
        return sortedBills.filter(bill => !bill.is_default);
    }, [sortedBills]);

    // Fetch bills for the expanded payment group
    // This single request returns all bills in the group with balances included
    // expandedPaymentGroupId can be 'default_group' (null) or a group UUID
    const { data: paymentBillsData, isLoading: isLoadingPaymentBills } = useQuery(
        ['templates', expandedPaymentGroupId],
        () => listUsersTemplates(csrfToken, expandedPaymentGroupId === 'default_group' ? null : expandedPaymentGroupId),
        {
            enabled: !!expandedPaymentGroupId && !!csrfToken,
            staleTime: 5 * 60 * 1000,
        }
    );

    const allPaymentBills = paymentBillsData || [];

    // Fetch bills for the take payment expanded group
    // expandedTakePaymentGroupId can be 'default_group' (null) or a group UUID
    const { data: takePaymentBillsData } = useQuery(
        ['templates', expandedTakePaymentGroupId],
        () => listUsersTemplates(csrfToken, expandedTakePaymentGroupId === 'default_group' ? null : expandedTakePaymentGroupId),
        {
            enabled: !!expandedTakePaymentGroupId && !!csrfToken,
            staleTime: 5 * 60 * 1000,
        }
    );

    const allTakePaymentBills = takePaymentBillsData || [];

    // Filter to unarchived bills owned by current user, and sort by favorite status (favorites first), then by created_at (newest first)
    const sortedTakePaymentBills = useMemo(() => {
        if (!allTakePaymentBills || allTakePaymentBills.length === 0) return [];
        
        // Filter to only unarchived bills owned by the current user
        const ownedUnarchivedBills = allTakePaymentBills.filter(bill => {
            if (bill.archived_at && bill.archived_at !== '') return false;
            return bill.owner_sub && authClaims?.user_sub && 
                   bill.owner_sub.toUpperCase() === authClaims.user_sub.toUpperCase();
        });
        
        // Sort by favorite first, then by created_at date (newest first)
        return ownedUnarchivedBills.sort((a, b) => {
            const favoriteA = getFavoriteStatus(a);
            const favoriteB = getFavoriteStatus(b);
            if (favoriteA !== favoriteB) {
                return favoriteA ? -1 : 1;
            }
            const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
            const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
            return dateB - dateA; // Newest first
        });
    }, [allTakePaymentBills, authClaims?.user_sub]);

    // Fetch statements for the selected take payment bill
    const { data: takePaymentStatements } = useQuery(
        ['statements', selectedTakePaymentBill?.id],
        listTemplateStatements(csrfToken),
        {
            enabled: !!selectedTakePaymentBill?.id && !!csrfToken,
            staleTime: 5 * 60 * 1000,
        }
    );

    // Fetch transactions for the selected take payment bill
    const { data: takePaymentTransactions } = useQuery(
        ['transactions', selectedTakePaymentBill?.id],
        listTemplateTransactions(csrfToken),
        {
            enabled: !!selectedTakePaymentBill?.id && !!csrfToken,
            staleTime: 5 * 60 * 1000,
        }
    );

    // Fetch statements for the selected payment bill (Pay Bill modal)
    const { data: paymentBillStatements } = useQuery(
        ['statements', selectedPaymentBill?.id],
        listTemplateStatements(csrfToken),
        {
            enabled: !!selectedPaymentBill?.id && !!csrfToken,
            staleTime: 5 * 60 * 1000,
        }
    );

    // Fetch transactions for the selected payment bill (Pay Bill modal)
    const { data: paymentBillTransactions } = useQuery(
        ['transactions', selectedPaymentBill?.id],
        listTemplateTransactions(csrfToken),
        {
            enabled: !!selectedPaymentBill?.id && !!csrfToken,
            staleTime: 5 * 60 * 1000,
        }
    );

    // Balance is now returned directly from the API, no need for state management

    // Get user balance from bill participants (show all bills, not just those with negative balance)
    const sortedPaymentBills = useMemo(() => {
        if (!allPaymentBills || allPaymentBills.length === 0) {
            return [];
        }

        // Filter to only unarchived bills
        const unarchivedBills = allPaymentBills.filter(bill => !bill.archived_at || bill.archived_at === '');

        // Sort by favorite status (favorites first), then by created_at (newest first)
        return unarchivedBills.sort((a, b) => {
            const favoriteA = getFavoriteStatus(a);
            const favoriteB = getFavoriteStatus(b);
            if (favoriteA !== favoriteB) {
                return favoriteA ? -1 : 1;
            }
            const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
            const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
            return dateB - dateA; // Newest first
        });
    }, [allPaymentBills]);

    // In default group: exclude shadow (is_default) bills so we show one "Default Bill" row instead
    const paymentBillsInDefaultGroup = useMemo(() => {
        return sortedPaymentBills.filter(bill => !bill.is_default);
    }, [sortedPaymentBills]);

    // In default group: exclude shadow (is_default) bills so we show one "Default Bill" row instead
    const takePaymentBillsInDefaultGroup = useMemo(() => {
        return sortedTakePaymentBills.filter(bill => !bill.is_default);
    }, [sortedTakePaymentBills]);

    const handleNewChargeClick = (event) => {
        // Only prevent default on touch devices to avoid interfering with click on Android
        if (event.type === 'touchstart') {
            event.preventDefault();
        }
        event.stopPropagation();
        setNewChargeMenuAnchor(event.currentTarget);
        setShowNewChargeSubmenu(true);
        setExpandedGroupId('default_group'); // Auto-expand default group
    };

    const handleNewChargeTouch = (event) => {
        // Prevent default only to avoid double-firing, but allow click to work
        event.stopPropagation();
        setNewChargeMenuAnchor(event.currentTarget);
        setShowNewChargeSubmenu(true);
        setExpandedGroupId('default_group'); // Auto-expand default group
    };

    const handleNewChargeSubmenuClose = () => {
        setShowNewChargeSubmenu(false);
        setNewChargeMenuAnchor(null);
        setExpandedGroupId(null);
    };

    const handleMakePaymentClick = (event) => {
        // Only prevent default on touch devices to avoid interfering with click on Android
        if (event.type === 'touchstart') {
            event.preventDefault();
        }
        event.stopPropagation();
        setMakePaymentMenuAnchor(event.currentTarget);
        setShowMakePaymentSubmenu(true);
        setExpandedPaymentGroupId('default_group'); // Auto-expand default group
    };

    const handleMakePaymentTouch = (event) => {
        // Prevent default only to avoid double-firing, but allow click to work
        event.stopPropagation();
        setMakePaymentMenuAnchor(event.currentTarget);
        setShowMakePaymentSubmenu(true);
        setExpandedPaymentGroupId('default_group'); // Auto-expand default group
    };

    const handleMakePaymentSubmenuClose = () => {
        setShowMakePaymentSubmenu(false);
        setMakePaymentMenuAnchor(null);
        setExpandedPaymentGroupId(null);
    };

    const handleTakePaymentClick = (event) => {
        if (event.type === 'touchstart') {
            event.preventDefault();
        }
        event.stopPropagation();
        setTakePaymentMenuAnchor(event.currentTarget);
        setShowTakePaymentSubmenu(true);
        setExpandedTakePaymentGroupId('default_group'); // Auto-expand default group
    };

    const handleTakePaymentTouch = (event) => {
        event.stopPropagation();
        setTakePaymentMenuAnchor(event.currentTarget);
        setShowTakePaymentSubmenu(true);
        setExpandedTakePaymentGroupId('default_group'); // Auto-expand default group
    };

    const handleTakePaymentSubmenuClose = () => {
        setShowTakePaymentSubmenu(false);
        setTakePaymentMenuAnchor(null);
        setExpandedTakePaymentGroupId(null);
        setShowTakePaymentUserMenu(false);
        setTakePaymentUserMenuAnchor(null);
        // Don't clear selectedTakePaymentBill here - it's needed for the modal
    };

    const handleGroupClick = (groupId) => {
        // Handle "default_group" option (bills without a group_id)
        if (groupId === 'default_group' || groupId === null) {
            setExpandedGroupId('default_group');
            return;
        }
        if (expandedGroupId === groupId) {
            setExpandedGroupId(null);
        } else {
            setExpandedGroupId(groupId);
        }
    };

    const handlePaymentGroupClick = (groupId) => {
        // Handle "default_group" option (bills without a group_id)
        if (groupId === 'default_group' || groupId === null) {
            setExpandedPaymentGroupId('default_group');
            return;
        }
        if (expandedPaymentGroupId === groupId) {
            setExpandedPaymentGroupId(null);
        } else {
            setExpandedPaymentGroupId(groupId);
        }
    };

    const handleTakePaymentGroupClick = (groupId) => {
        // Handle "default_group" option (bills without a group_id)
        if (groupId === 'default_group' || groupId === null) {
            setExpandedTakePaymentGroupId('default_group');
            return;
        }
        if (expandedTakePaymentGroupId === groupId) {
            setExpandedTakePaymentGroupId(null);
        } else {
            setExpandedTakePaymentGroupId(groupId);
        }
    };

    const handleBillClick = (bill) => {
        if (bill?.id === DEFAULT_BILL_MENU_ID) {
            setShowQuickSplitModal(true);
            handleNewChargeSubmenuClose();
            onClose();
            return;
        }
        setSelectedBill(bill);
        setShowStatementModal(true);
        handleNewChargeSubmenuClose();
        onClose(); // Close the main menu when opening the modal
    };

    const handlePaymentBillClick = (bill) => {
        setSelectedPaymentBill(bill);
        setShowPaymentModal(true);
        handleMakePaymentSubmenuClose();
        onClose();
    };

    const handleTakePaymentBillClick = (bill, event) => {
        // When clicking a bill in take payment flow, open the user selection menu
        setSelectedTakePaymentBill(bill);
        setTakePaymentUserMenuAnchor(event.currentTarget);
        setShowTakePaymentUserMenu(true);
    };

    const handleTakePaymentUserClick = (user, balance) => {
        // First close all menus
        setShowTakePaymentUserMenu(false);
        setShowTakePaymentSubmenu(false);
        setTakePaymentMenuAnchor(null);
        onClose();

        // Then open modal with user data after a brief delay to ensure menus close
        setTimeout(() => {
            setSelectedTakePaymentUser({ user, balance });
            setShowTakePaymentModal(true);
        }, 50);
    };

    const handlePaymentModalClose = () => {
        setShowPaymentModal(false);
        setSelectedPaymentBill(null);
    };

    const handleTakePaymentModalClose = () => {
        setShowTakePaymentModal(false);
        setSelectedTakePaymentUser(null);
        setSelectedTakePaymentBill(null);
        setExpandedTakePaymentGroupId(null);
    };

    const handleStatementModalClose = () => {
        setShowStatementModal(false);
        setSelectedBill(null);
    };

    const handleQuickSplitClick = () => {
        setShowQuickSplitModal(true);
        onClose();
    };

    const handleQuickSplitModalClose = () => {
        setShowQuickSplitModal(false);
    };

    const handleMainMenuClose = () => {
        handleNewChargeSubmenuClose();
        handleMakePaymentSubmenuClose();
        handleTakePaymentSubmenuClose();
        onClose();
    };

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMainMenuClose}
                disableAutoFocus
                disableEnforceFocus
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        backgroundColor: theme.colors.menuBg || theme.colors.backgroundPrimary,
                        color: theme.colors.menuText || theme.colors.text,
                        minWidth: 'auto',
                        touchAction: 'manipulation',
                        '& .MuiMenuItem-root': {
                            paddingRight: '12px',
                            paddingLeft: '12px',
                            touchAction: 'manipulation',
                            WebkitTapHighlightColor: 'transparent',
                        },
                    }
                }}
            >
                <MenuItem
                    onClick={handleNewChargeClick}
                    onTouchStart={(e) => {
                        // On touch devices, use touch handler; on Android, let click handle it
                        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                        if (isIOS) {
                            handleNewChargeTouch(e);
                        }
                    }}
                    sx={{
                        paddingRight: '12px',
                        paddingLeft: '12px',
                        touchAction: 'manipulation',
                        WebkitTapHighlightColor: 'transparent',
                        '&:hover': {
                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: '8px' }}>
                        <Box sx={{ width: '16px', minWidth: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CaretLeft size={16} color={theme.colors.text} />
                        </Box>
                        <Typography sx={{ color: theme.colors.text, flex: 1 }}>Charge</Typography>
                        <Box sx={{ width: '16px', minWidth: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {/* Right side space for symmetry */}
                        </Box>
                    </Box>
                </MenuItem>
                <MenuItem
                    onClick={handleMakePaymentClick}
                    onTouchStart={(e) => {
                        // On touch devices, use touch handler; on Android, let click handle it
                        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                        if (isIOS) {
                            handleMakePaymentTouch(e);
                        }
                    }}
                    sx={{
                        paddingRight: '12px',
                        paddingLeft: '12px',
                        touchAction: 'manipulation',
                        WebkitTapHighlightColor: 'transparent',
                        '&:hover': {
                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: '8px' }}>
                        <Box sx={{ width: '16px', minWidth: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CaretLeft size={16} color={theme.colors.text} />
                        </Box>
                        <Typography sx={{ color: theme.colors.text, flex: 1 }}>Pay</Typography>
                        <Box sx={{ width: '16px', minWidth: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {/* Right side space for symmetry */}
                        </Box>
                    </Box>
                </MenuItem>
                <MenuItem
                    onClick={handleTakePaymentClick}
                    onTouchStart={(e) => {
                        // On touch devices, use touch handler; on Android, let click handle it
                        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                        if (isIOS) {
                            handleTakePaymentTouch(e);
                        }
                    }}
                    sx={{
                        paddingRight: '12px',
                        paddingLeft: '12px',
                        touchAction: 'manipulation',
                        WebkitTapHighlightColor: 'transparent',
                        '&:hover': {
                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: '8px' }}>
                        <Box sx={{ width: '16px', minWidth: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CaretLeft size={16} color={theme.colors.text} />
                        </Box>
                        <Typography sx={{ color: theme.colors.text, flex: 1 }}>Collect</Typography>
                        <Box sx={{ width: '16px', minWidth: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {/* Right side space for symmetry */}
                        </Box>
                    </Box>
                </MenuItem>
                <MenuItem
                    onClick={handleQuickSplitClick}
                    sx={{
                        paddingRight: '12px',
                        paddingLeft: '12px',
                        touchAction: 'manipulation',
                        WebkitTapHighlightColor: 'transparent',
                        '&:hover': {
                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: '8px' }}>
                        <Box sx={{ width: '16px', minWidth: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CaretLeft size={16} color={theme.colors.text} />
                        </Box>
                        <Typography sx={{ color: theme.colors.text, flex: 1 }}>Quick Split</Typography>
                        <Box sx={{ width: '16px', minWidth: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {/* Right side space for symmetry */}
                        </Box>
                    </Box>
                </MenuItem>
            </Menu>

            {/* Submenu for New Charge - Groups */}
            <Menu
                open={showNewChargeSubmenu}
                anchorEl={newChargeMenuAnchor}
                onClose={handleNewChargeSubmenuClose}
                disableAutoFocus
                disableEnforceFocus
                anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
                transformOrigin={{ vertical: 'center', horizontal: 'right' }}
                disableScrollLock
                PaperProps={{
                    sx: {
                        backgroundColor: theme.colors.menuBg || theme.colors.backgroundPrimary,
                        color: theme.colors.menuText || theme.colors.text,
                        minWidth: '250px',
                        maxWidth: '400px',
                        maxHeight: 'calc(var(--vh, 1vh) * 100 - 100px)',
                        overflow: 'auto',
                        touchAction: 'manipulation',
                    }
                }}
            >
                <Box sx={{ p: 1 }}>
                    {(() => {
                        return (
                            <>
                                {/* "Default Group" option for bills without a group */}
                                <Box key="default_group">
                                    <ListItemButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleGroupClick('default_group');
                                        }}
                                        onTouchStart={(e) => {
                                            // Only handle touch on iOS to avoid interfering with Android clicks
                                            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                                            if (isIOS) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleGroupClick('default_group');
                                            }
                                        }}
                                        sx={{
                                            touchAction: 'manipulation',
                                            WebkitTapHighlightColor: 'transparent',
                                            '&:hover': {
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary="Default Group"
                                            primaryTypographyProps={{
                                                sx: { color: theme.colors.text, fontWeight: 600 }
                                            }}
                                        />
                                        {expandedGroupId === 'default_group' ? (
                                            <CaretDown size={20} color={theme.colors.text} />
                                        ) : (
                                            <CaretRight size={20} color={theme.colors.text} />
                                        )}
                                    </ListItemButton>
                                    <Collapse in={expandedGroupId === 'default_group'} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                            {/* Single "Default Bill" row (replaces multiple shadow bills) */}
                                            <ListItemButton
                                                key={DEFAULT_BILL_MENU_ID}
                                                onClick={() => handleBillClick({ id: DEFAULT_BILL_MENU_ID, name: 'Default Bill' })}
                                                sx={{
                                                    pl: 4,
                                                    '&:hover': {
                                                        backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                    },
                                                }}
                                            >
                                                <ListItemText
                                                    primary="Default Bill"
                                                    primaryTypographyProps={{
                                                        sx: { color: theme.colors.text, fontSize: '0.875rem' }
                                                    }}
                                                />
                                            </ListItemButton>
                                            {billsInDefaultGroup.map((bill) => (
                                                <ListItemButton
                                                    key={bill.id}
                                                    onClick={() => handleBillClick(bill)}
                                                    sx={{
                                                        pl: 4,
                                                        '&:hover': {
                                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                        },
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={bill.name}
                                                        primaryTypographyProps={{
                                                            sx: { color: theme.colors.text, fontSize: '0.875rem' }
                                                        }}
                                                    />
                                                </ListItemButton>
                                            ))}
                                        </List>
                                    </Collapse>
                                </Box>
                                {/* Regular groups */}
                                {allGroupsForMenu.map((group) => {
                                    const displayName = group.displayName || group.name;
                                    return (
                                <Box key={group.id}>
                                    <ListItemButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleGroupClick(group.id);
                                        }}
                                        onTouchStart={(e) => {
                                            // Only handle touch on iOS to avoid interfering with Android clicks
                                            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                                            if (isIOS) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleGroupClick(group.id);
                                            }
                                        }}
                                        sx={{
                                            touchAction: 'manipulation',
                                            WebkitTapHighlightColor: 'transparent',
                                            '&:hover': {
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={displayName}
                                            primaryTypographyProps={{
                                                sx: { color: theme.colors.text }
                                            }}
                                        />
                                        {expandedGroupId === group.id ? (
                                            <CaretDown size={20} color={theme.colors.text} />
                                        ) : (
                                            <CaretRight size={20} color={theme.colors.text} />
                                        )}
                                    </ListItemButton>
                                    <Collapse in={expandedGroupId === group.id} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {sortedBills.length === 0 ? (
                                            <ListItem>
                                                <ListItemText
                                                    primary="No bills in this group"
                                                    primaryTypographyProps={{
                                                        sx: {
                                                            color: theme.colors.text,
                                                            fontStyle: 'italic',
                                                            fontSize: '0.875rem'
                                                        }
                                                    }}
                                                />
                                            </ListItem>
                                        ) : (
                                            sortedBills.map((bill) => (
                                                <ListItemButton
                                                    key={bill.id}
                                                    onClick={() => handleBillClick(bill)}
                                                    sx={{
                                                        pl: 4,
                                                        '&:hover': {
                                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                        },
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={bill.name}
                                                        primaryTypographyProps={{
                                                            sx: { color: theme.colors.text, fontSize: '0.875rem' }
                                                        }}
                                                    />
                                                </ListItemButton>
                                            ))
                                        )}
                                    </List>
                                </Collapse>
                            </Box>
                                );
                            })}
                            </>
                        );
                    })()}
                </Box>
            </Menu>

            {/* Submenu for Make Payment - Groups */}
            <Menu
                open={showMakePaymentSubmenu}
                anchorEl={makePaymentMenuAnchor}
                onClose={handleMakePaymentSubmenuClose}
                disableAutoFocus
                disableEnforceFocus
                anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
                transformOrigin={{ vertical: 'center', horizontal: 'right' }}
                disableScrollLock
                PaperProps={{
                    sx: {
                        backgroundColor: theme.colors.menuBg || theme.colors.backgroundPrimary,
                        color: theme.colors.menuText || theme.colors.text,
                        minWidth: '250px',
                        maxWidth: '400px',
                        maxHeight: 'calc(var(--vh, 1vh) * 100 - 100px)',
                        overflow: 'auto',
                        touchAction: 'manipulation',
                    }
                }}
            >
                <Box sx={{ p: 1 }}>
                    {(() => {
                        return (
                            <>
                                {/* "Default Group" option for bills without a group */}
                                <Box key="default_group">
                                    <ListItemButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handlePaymentGroupClick('default_group');
                                        }}
                                        onTouchStart={(e) => {
                                            // Only handle touch on iOS to avoid interfering with Android clicks
                                            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                                            if (isIOS) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handlePaymentGroupClick('default_group');
                                            }
                                        }}
                                        sx={{
                                            touchAction: 'manipulation',
                                            WebkitTapHighlightColor: 'transparent',
                                            '&:hover': {
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary="Default Group"
                                            primaryTypographyProps={{
                                                sx: { color: theme.colors.text, fontWeight: 600 }
                                            }}
                                        />
                                        {expandedPaymentGroupId === 'default_group' ? (
                                            <CaretDown size={20} color={theme.colors.text} />
                                        ) : (
                                            <CaretRight size={20} color={theme.colors.text} />
                                        )}
                                    </ListItemButton>
                                    <Collapse in={expandedPaymentGroupId === 'default_group'} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                            {paymentBillsInDefaultGroup.length === 0 ? (
                                                <ListItem>
                                                    <ListItemText
                                                        primary="No bills in this group"
                                                        primaryTypographyProps={{
                                                            sx: {
                                                                color: theme.colors.text,
                                                                fontStyle: 'italic',
                                                                fontSize: '0.875rem'
                                                            }
                                                        }}
                                                    />
                                                </ListItem>
                                            ) : (
                                                paymentBillsInDefaultGroup.map((bill) => {
                                                const userParticipant = (bill.participants || []).find(p =>
                                                    p.user_sub && p.user_sub.toUpperCase() === authClaims?.user_sub?.toUpperCase()
                                                );
                                                const balance = userParticipant?.balance;

                                                return (
                                                    <ListItemButton
                                                        key={bill.id}
                                                        onClick={() => handlePaymentBillClick(bill)}
                                                        sx={{
                                                            pl: 4,
                                                            '&:hover': {
                                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                            },
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={bill.name}
                                                            primaryTypographyProps={{
                                                                sx: { color: theme.colors.text, fontSize: '0.875rem' }
                                                            }}
                                                        />
                                                        {balance !== undefined && (
                                                            <Typography
                                                                sx={{
                                                                    color: balance < 0 ? (theme.colors.error || '#f44336') : theme.colors.text,
                                                                    fontSize: '0.875rem',
                                                                    fontWeight: 500,
                                                                    ml: 2,
                                                                    minWidth: 'fit-content'
                                                                }}
                                                            >
                                                                ${FormatNumber(Math.abs(balance))}
                                                            </Typography>
                                                        )}
                                                    </ListItemButton>
                                                );
                                                })
                                            )}
                                        </List>
                                    </Collapse>
                                </Box>
                                {/* Regular groups */}
                                {allGroupsForMenu.map((group) => {
                                    const displayName = group.displayName || group.name;
                                    return (
                            <Box key={group.id}>
                                <ListItemButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handlePaymentGroupClick(group.id);
                                    }}
                                    onTouchStart={(e) => {
                                        // Only handle touch on iOS to avoid interfering with Android clicks
                                        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                                        if (isIOS) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handlePaymentGroupClick(group.id);
                                        }
                                    }}
                                    sx={{
                                        touchAction: 'manipulation',
                                        WebkitTapHighlightColor: 'transparent',
                                        '&:hover': {
                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                        },
                                    }}
                                >
                                    <ListItemText
                                        primary={displayName}
                                        primaryTypographyProps={{
                                            sx: { color: theme.colors.text }
                                        }}
                                    />
                                    {expandedPaymentGroupId === group.id ? (
                                        <CaretDown size={20} color={theme.colors.text} />
                                    ) : (
                                        <CaretRight size={20} color={theme.colors.text} />
                                    )}
                                </ListItemButton>
                                <Collapse in={expandedPaymentGroupId === group.id} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {allPaymentBills.length === 0 ? (
                                            <ListItem>
                                                <ListItemText
                                                    primary="No bills in this group"
                                                    primaryTypographyProps={{
                                                        sx: {
                                                            color: theme.colors.text,
                                                            fontStyle: 'italic',
                                                            fontSize: '0.875rem'
                                                        }
                                                    }}
                                                />
                                            </ListItem>
                                        ) : (
                                            <>
                                                {/* Balance is returned directly from the API in bill.participants[0].balance */}
                                                {sortedPaymentBills.length === 0 ? (
                                                                    <ListItem>
                                                                        <ListItemText
                                                                            primary="No bills in this group"
                                                                            primaryTypographyProps={{
                                                                                sx: {
                                                                                    color: theme.colors.text,
                                                                                    fontStyle: 'italic',
                                                                                    fontSize: '0.875rem'
                                                                                }
                                                                            }}
                                                                        />
                                                                    </ListItem>
                                                ) : (
                                                    sortedPaymentBills.map((bill) => {
                                                        // Find the current user's participant record to get balance
                                                        const userParticipant = (bill.participants || []).find(p =>
                                                            p.user_sub && p.user_sub.toUpperCase() === authClaims?.user_sub?.toUpperCase()
                                                        );
                                                        // Balance is always available from the API response
                                                        const balance = userParticipant?.balance;

                                                        return (
                                                            <ListItemButton
                                                                key={bill.id}
                                                                onClick={() => handlePaymentBillClick(bill)}
                                                                sx={{
                                                                    pl: 4,
                                                                    '&:hover': {
                                                                        backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                    },
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={bill.name}
                                                                    primaryTypographyProps={{
                                                                        sx: { color: theme.colors.text, fontSize: '0.875rem' }
                                                                    }}
                                                                />
                                                                {balance !== undefined && (
                                                                    <Typography
                                                                        sx={{
                                                                            color: balance < 0 ? (theme.colors.error || '#f44336') : theme.colors.text,
                                                                            fontSize: '0.875rem',
                                                                            fontWeight: 500,
                                                                            ml: 2,
                                                                            minWidth: 'fit-content'
                                                                        }}
                                                                    >
                                                                        ${FormatNumber(Math.abs(balance))}
                                                                    </Typography>
                                                                )}
                                                            </ListItemButton>
                                                        );
                                                    })
                                                )}
                                            </>
                                        )}
                                    </List>
                                </Collapse>
                            </Box>
                                );
                            })}
                            </>
                        );
                    })()}
                </Box>
            </Menu>

            {/* Submenu for Take Payment - Groups */}
            <Menu
                open={showTakePaymentSubmenu}
                anchorEl={takePaymentMenuAnchor}
                onClose={handleTakePaymentSubmenuClose}
                disableAutoFocus
                disableEnforceFocus
                anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
                transformOrigin={{ vertical: 'center', horizontal: 'right' }}
                disableScrollLock
                PaperProps={{
                    sx: {
                        backgroundColor: theme.colors.menuBg || theme.colors.backgroundPrimary,
                        color: theme.colors.menuText || theme.colors.text,
                        minWidth: '250px',
                        maxWidth: '400px',
                        maxHeight: 'calc(var(--vh, 1vh) * 100 - 100px)',
                        overflow: 'auto',
                        touchAction: 'manipulation',
                    }
                }}
            >
                <Box sx={{ p: 1 }}>
                    {(() => {
                        return (
                            <>
                                {/* "Default Group" option for bills without a group */}
                                <Box key="default_group">
                                    <ListItemButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleTakePaymentGroupClick('default_group');
                                        }}
                                        onTouchStart={(e) => {
                                            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                                            if (isIOS) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleTakePaymentGroupClick('default_group');
                                            }
                                        }}
                                        sx={{
                                            touchAction: 'manipulation',
                                            WebkitTapHighlightColor: 'transparent',
                                            '&:hover': {
                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary="Default Group"
                                            primaryTypographyProps={{
                                                sx: { color: theme.colors.text, fontWeight: 600 }
                                            }}
                                        />
                                        {expandedTakePaymentGroupId === 'default_group' ? (
                                            <CaretDown size={20} color={theme.colors.text} />
                                        ) : (
                                            <CaretRight size={20} color={theme.colors.text} />
                                        )}
                                    </ListItemButton>
                                    <Collapse in={expandedTakePaymentGroupId === 'default_group'} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                            {takePaymentBillsInDefaultGroup.length === 0 ? (
                                                <ListItem>
                                                    <ListItemText
                                                        primary="No bills in this group"
                                                        primaryTypographyProps={{
                                                            sx: {
                                                                color: theme.colors.text,
                                                                fontStyle: 'italic',
                                                                fontSize: '0.875rem'
                                                            }
                                                        }}
                                                    />
                                                </ListItem>
                                            ) : (
                                                takePaymentBillsInDefaultGroup.map((bill) => {
                                                const totalOutstanding = (bill.participants || []).reduce((sum, participant) => {
                                                    if (participant.user_sub && authClaims?.user_sub &&
                                                        participant.user_sub.toUpperCase() === authClaims.user_sub.toUpperCase()) {
                                                        return sum;
                                                    }
                                                    const balance = participant.balance;
                                                    if (balance !== undefined && balance < 0) {
                                                        return sum + Math.abs(balance);
                                                    }
                                                    return sum;
                                                }, 0);

                                                return (
                                                    <ListItemButton
                                                        key={bill.id}
                                                        onClick={(e) => handleTakePaymentBillClick(bill, e)}
                                                        sx={{
                                                            pl: 4,
                                                            '&:hover': {
                                                                backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                            },
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                                                            <CaretLeft size={16} color={theme.colors.text} />
                                                        </Box>
                                                        <ListItemText
                                                            primary={bill.name}
                                                            primaryTypographyProps={{
                                                                sx: { color: theme.colors.text, fontSize: '0.875rem' }
                                                            }}
                                                        />
                                                        {totalOutstanding > 0 && (
                                                            <Typography
                                                                sx={{
                                                                    color: theme.colors.error || '#f44336',
                                                                    fontSize: '0.875rem',
                                                                    fontWeight: 500,
                                                                    ml: 2,
                                                                    minWidth: 'fit-content'
                                                                }}
                                                            >
                                                                ${FormatNumber(totalOutstanding)}
                                                            </Typography>
                                                        )}
                                                    </ListItemButton>
                                                );
                                                })
                                            )}
                                        </List>
                                    </Collapse>
                                </Box>
                                {/* Regular groups */}
                                {allGroupsForMenu.map((group) => {
                                    const displayName = group.displayName || group.name;
                                    return (
                                        <Box key={group.id}>
                                            <ListItemButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleTakePaymentGroupClick(group.id);
                                                }}
                                                onTouchStart={(e) => {
                                                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                                                    if (isIOS) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleTakePaymentGroupClick(group.id);
                                                    }
                                                }}
                                                sx={{
                                                    touchAction: 'manipulation',
                                                    WebkitTapHighlightColor: 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                    },
                                                }}
                                            >
                                                <ListItemText
                                                    primary={displayName}
                                                    primaryTypographyProps={{
                                                        sx: { color: theme.colors.text }
                                                    }}
                                                />
                                                {expandedTakePaymentGroupId === group.id ? (
                                                    <CaretDown size={20} color={theme.colors.text} />
                                                ) : (
                                                    <CaretRight size={20} color={theme.colors.text} />
                                                )}
                                            </ListItemButton>
                                            <Collapse in={expandedTakePaymentGroupId === group.id} timeout="auto" unmountOnExit>
                                                <List component="div" disablePadding>
                                                    {sortedTakePaymentBills.length === 0 ? (
                                                        <ListItem>
                                                            <ListItemText
                                                                primary="No bills in this group"
                                                                primaryTypographyProps={{
                                                                    sx: {
                                                                        color: theme.colors.text,
                                                                        fontStyle: 'italic',
                                                                        fontSize: '0.875rem'
                                                                    }
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ) : (
                                                        sortedTakePaymentBills.map((bill) => {
                                                            // Calculate total outstanding balance for this bill (excluding current user)
                                                            const totalOutstanding = (bill.participants || []).reduce((sum, participant) => {
                                                                // Skip the current user
                                                                if (participant.user_sub && authClaims?.user_sub &&
                                                                    participant.user_sub.toUpperCase() === authClaims.user_sub.toUpperCase()) {
                                                                    return sum;
                                                                }
                                                                const balance = participant.balance;
                                                                // Only sum negative balances (amounts owed by other users)
                                                                if (balance !== undefined && balance < 0) {
                                                                    return sum + Math.abs(balance);
                                                                }
                                                                return sum;
                                                            }, 0);

                                                            return (
                                                                <ListItemButton
                                                                    key={bill.id}
                                                                    onClick={(e) => handleTakePaymentBillClick(bill, e)}
                                                                    sx={{
                                                                        pl: 4,
                                                                        '&:hover': {
                                                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                                                        },
                                                                    }}
                                                                >
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                                                                        <CaretLeft size={16} color={theme.colors.text} />
                                                                    </Box>
                                                                    <ListItemText
                                                                        primary={bill.name}
                                                                        primaryTypographyProps={{
                                                                            sx: { color: theme.colors.text, fontSize: '0.875rem' }
                                                                        }}
                                                                    />
                                                                    {totalOutstanding > 0 && (
                                                                        <Typography
                                                                            sx={{
                                                                                color: theme.colors.error || '#f44336',
                                                                                fontSize: '0.875rem',
                                                                                fontWeight: 500,
                                                                                ml: 2,
                                                                                minWidth: 'fit-content'
                                                                            }}
                                                                        >
                                                                            ${FormatNumber(totalOutstanding)}
                                                                        </Typography>
                                                                    )}
                                                                </ListItemButton>
                                                            );
                                                        })
                                                    )}
                                                </List>
                                            </Collapse>
                                        </Box>
                                    );
                                })}
                            </>
                        );
                    })()}
                </Box>
            </Menu>

            {/* Submenu for Take Payment - Users with outstanding balances */}
            <Menu
                open={showTakePaymentUserMenu}
                anchorEl={takePaymentUserMenuAnchor}
                onClose={() => setShowTakePaymentUserMenu(false)}
                disableAutoFocus
                disableEnforceFocus
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        backgroundColor: theme.colors.menuBg || theme.colors.backgroundPrimary,
                        color: theme.colors.menuText || theme.colors.text,
                        minWidth: '250px',
                        maxWidth: '400px',
                        maxHeight: '600px',
                        overflow: 'auto',
                        touchAction: 'manipulation',
                    }
                }}
            >
                <Box sx={{ p: 1 }}>
                    <Typography variant="subtitle2" sx={{ color: theme.colors.text, fontWeight: 600, mb: 1, px: 1 }}>
                        Select User
                    </Typography>
                    {selectedTakePaymentBill && (() => {
                        // Get all participants with negative balances (they owe money), excluding current user
                        const participantsWithBalance = (selectedTakePaymentBill.participants || []).filter(p => {
                            // Exclude the current user
                            if (p.user_sub && authClaims?.user_sub &&
                                p.user_sub.toUpperCase() === authClaims.user_sub.toUpperCase()) {
                                return false;
                            }
                            const balance = p.balance;
                            return balance !== undefined && balance < 0;
                        });

                        if (participantsWithBalance.length === 0) {
                            return (
                                <ListItem>
                                    <ListItemText
                                        primary="No outstanding balances"
                                        primaryTypographyProps={{
                                            sx: {
                                                color: theme.colors.text,
                                                fontStyle: 'italic',
                                                fontSize: '0.875rem'
                                            }
                                        }}
                                    />
                                </ListItem>
                            );
                        }

                        return participantsWithBalance.map((participant) => {
                            const balance = participant.balance;
                            // Participant objects have: email, user_sub, id, balance
                            const displayName = participant.email || (participant.user_sub ? `User ${participant.user_sub.substring(0, 8)}...` : 'Unknown User');

                            return (
                                <ListItemButton
                                    key={participant.id || participant.user_sub || participant.email}
                                    onClick={() => handleTakePaymentUserClick(participant, balance)}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)',
                                        },
                                    }}
                                >
                                    <ListItemText
                                        primary={displayName}
                                        primaryTypographyProps={{
                                            sx: { color: theme.colors.text, fontSize: '0.875rem' }
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            color: theme.colors.error || '#f44336',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            ml: 2,
                                            minWidth: 'fit-content'
                                        }}
                                    >
                                        ${FormatNumber(Math.abs(balance))}
                                    </Typography>
                                </ListItemButton>
                            );
                        });
                    })()}
                </Box>
            </Menu>

            {/* New Statement Modal */}
            {showStatementModal && selectedBill && (
                <Suspense fallback={<div>Loading...</div>}>
                    <NewStatementModal
                        open={showStatementModal}
                        onClose={handleStatementModalClose}
                        templateId={selectedBill.id}
                    />
                </Suspense>
            )}

            {/* Payment Modal */}
            {showPaymentModal && selectedPaymentBill && (() => {
                // Create a mock transaction item for the payment modal
                // The modal expects an item with bill_id, user, etc.
                const userParticipant = (selectedPaymentBill.participants || []).find(p =>
                    p.user_sub && p.user_sub.toUpperCase() === authClaims?.user_sub?.toUpperCase()
                );
                // Balance is always available from the API response
                const balance = userParticipant?.balance;

                // Check if current user is the owner (we need to get owner_sub from the bill)
                // For now, assume participant is not owner (since they're making a payment)
                const isOwner = false;

                // Create mock item for the transaction modal
                const mockItem = {
                    bill_id: selectedPaymentBill.id,
                    user: {
                        sub: authClaims?.user_sub,
                        email: userParticipant?.email || authClaims?.email || ''
                    },
                    amount: balance || 0,
                    statement_id: null
                };

                return (
                    <TransactionModal
                        open={showPaymentModal}
                        handleClose={handlePaymentModalClose}
                        item={mockItem}
                        isOwner={isOwner}
                        templateId={selectedPaymentBill.id}
                        statements={paymentBillStatements?.items ?? paymentBillStatements ?? []}
                        transactions={paymentBillTransactions?.items ?? paymentBillTransactions ?? []}
                    />
                );
            })()}

            {/* Quick Split Modal */}
            {showQuickSplitModal && (
                <Suspense fallback={<div>Loading...</div>}>
                    <QuickSplitModal
                        open={showQuickSplitModal}
                        onClose={handleQuickSplitModalClose}
                    />
                </Suspense>
            )}

            {/* Take Payment Modal */}
            {showTakePaymentModal && selectedTakePaymentUser && selectedTakePaymentBill && (() => {
                // selectedTakePaymentUser contains { user: participant, balance }
                // where participant has: email, user_sub, id, balance
                const { user: participant, balance } = selectedTakePaymentUser;

                // Create mock item for the transaction modal
                // The modal expects item.user to have { sub, email }
                const mockItem = {
                    bill_id: selectedTakePaymentBill.id,
                    user: {
                        sub: participant.user_sub,
                        email: participant.email || ''
                    },
                    amount: balance || 0,
                    statement_id: null
                };

                // The current user is taking payment, so they are the owner
                const isOwner = true;

                return (
                    <TransactionModal
                        open={showTakePaymentModal}
                        handleClose={handleTakePaymentModalClose}
                        item={mockItem}
                        isOwner={isOwner}
                        templateId={selectedTakePaymentBill.id}
                        statements={takePaymentStatements?.items ?? takePaymentStatements ?? []}
                        transactions={takePaymentTransactions?.items ?? takePaymentTransactions ?? []}
                    />
                );
            })()}
        </>
    );
};

export default QuickActionsMenu;
